import {
  Building2,
  CalendarDays,
  Camera,
  Check,
  IdCard,
  Mail,
  MessageCircle,
  RotateCcw,
  Save,
  ShieldCheck,
  UserRound,
  Warehouse,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { UserProfile } from "../types";

interface ProfilePageProps {
  profile: UserProfile;
  onSave: (profile: UserProfile) => void;
  onOpenSupport: () => void;
}

const emptyProfile: UserProfile = {
  id: "",
  name: "",
  gender: "",
  birthday: "",
  company: "",
  role: "",
  email: "",
  avatarDataUrl: "",
  warehouse: "昆山大仓",
};

export function ProfilePage({ profile, onSave, onOpenSupport }: ProfilePageProps) {
  const [draft, setDraft] = useState<UserProfile>(profile);
  const [message, setMessage] = useState("");
  const [avatarError, setAvatarError] = useState("");
  const avatarInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => setDraft(profile), [profile]);

  const completedFields = [draft.id, draft.name, draft.company, draft.role, draft.email, draft.warehouse].filter(Boolean).length;
  const completion = Math.round((completedFields / 6) * 100);

  function updateField<K extends keyof UserProfile>(key: K, value: UserProfile[K]) {
    setDraft((current) => ({ ...current, [key]: value }));
    setMessage("");
  }

  function importAvatar(file: File) {
    if (!file.type.startsWith("image/")) {
      setAvatarError("请选择 JPG、PNG 或 WebP 图片。");
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      setAvatarError("头像文件请控制在 4MB 以内。");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      updateField("avatarDataUrl", typeof reader.result === "string" ? reader.result : "");
      setAvatarError("");
    };
    reader.onerror = () => setAvatarError("头像读取失败，请重新选择。");
    reader.readAsDataURL(file);
  }

  function save() {
    onSave(draft);
    setMessage("个人资料已保存在当前浏览器");
    window.setTimeout(() => setMessage(""), 3200);
  }

  function clearProfile() {
    setDraft({ ...emptyProfile, warehouse: profile.warehouse || emptyProfile.warehouse });
    setMessage("表单已恢复为空白，点击保存后生效");
  }

  return (
    <div className="profile-page">
      <section className="profile-hero">
        <div className="profile-hero-monogram">{draft.name?.slice(0, 1) || "仓"}</div>
        <div>
          <span className="section-kicker">IDENTITY & WORKSPACE</span>
          <h2>你的工作空间，<br />从一个准确的身份开始。</h2>
          <p>资料用于区分团队账号与仓库视图。性别和生日为选填项，头像只在当前浏览器本地保存。</p>
        </div>
        <div className="profile-completion">
          <span>资料完整度</span>
          <strong>{completion}%</strong>
          <div><i style={{ width: `${completion}%` }} /></div>
          <small>补全姓名、公司、角色、邮箱与仓库</small>
        </div>
      </section>

      <section className="profile-workbench">
        <aside className="avatar-panel">
          <div className="avatar-preview">
            {draft.avatarDataUrl ? <img src={draft.avatarDataUrl} alt="用户头像" /> : <UserRound size={54} strokeWidth={1.1} />}
            <button onClick={() => avatarInputRef.current?.click()} aria-label="导入头像"><Camera size={17} /></button>
          </div>
          <h3>{draft.name || "未命名用户"}</h3>
          <p>{draft.role || "填写你的工作角色"}</p>
          <button className="secondary-button full" onClick={() => avatarInputRef.current?.click()}>
            <Camera size={16} /> 导入头像
          </button>
          {draft.avatarDataUrl && (
            <button className="text-link" onClick={() => updateField("avatarDataUrl", "")}>
              <RotateCcw size={14} /> 移除头像
            </button>
          )}
          <button className="secondary-button full profile-support-button" onClick={onOpenSupport}>
            <MessageCircle size={16} /> 联系仓储客服
          </button>
          {avatarError && <div className="field-error" role="alert">{avatarError}</div>}
          <div className="profile-privacy">
            <ShieldCheck size={17} />
            <span><strong>本地资料</strong><small>头像与资料不会上传到第三方服务</small></span>
          </div>
        </aside>

        <form className="profile-form" onSubmit={(event) => { event.preventDefault(); save(); }}>
          <div className="form-section-head">
            <div><span className="section-kicker">BASIC INFORMATION</span><h3>基本信息</h3></div>
            <span>公开信息仅用于当前应用</span>
          </div>

          <div className="form-grid">
            <label>
              <span><IdCard size={15} /> 用户 ID</span>
              <input value={draft.id} onChange={(event) => updateField("id", event.target.value)} placeholder="例如 CX-2026-001" autoComplete="username" />
            </label>
            <label>
              <span><UserRound size={15} /> 姓名</span>
              <input value={draft.name} onChange={(event) => updateField("name", event.target.value)} placeholder="你的姓名" autoComplete="name" />
            </label>
            <label>
              <span>性别 <small>选填</small></span>
              <select value={draft.gender} onChange={(event) => updateField("gender", event.target.value)}>
                <option value="">不填写</option>
                <option value="女">女</option>
                <option value="男">男</option>
                <option value="其他">其他</option>
                <option value="不愿透露">不愿透露</option>
              </select>
            </label>
            <label>
              <span><CalendarDays size={15} /> 生日 <small>选填</small></span>
              <input type="date" value={draft.birthday} onChange={(event) => updateField("birthday", event.target.value)} />
            </label>
            <label>
              <span><Building2 size={15} /> 公司名称</span>
              <input value={draft.company} onChange={(event) => updateField("company", event.target.value)} placeholder="公司或团队名称" autoComplete="organization" />
            </label>
            <label>
              <span>岗位 / 角色</span>
              <input value={draft.role} onChange={(event) => updateField("role", event.target.value)} placeholder="例如 仓储运营负责人" />
            </label>
            <label>
              <span><Mail size={15} /> 邮箱</span>
              <input type="email" value={draft.email} onChange={(event) => updateField("email", event.target.value)} placeholder="name@company.com" autoComplete="email" />
            </label>
            <label>
              <span><Warehouse size={15} /> 默认仓库</span>
              <input value={draft.warehouse} onChange={(event) => updateField("warehouse", event.target.value)} placeholder="例如 昆山大仓" />
            </label>
          </div>

          <div className="form-footer">
            <div>
              {message && <span className="save-feedback" role="status"><Check size={15} />{message}</span>}
            </div>
            <button type="button" className="ghost-button" onClick={clearProfile}>清空表单</button>
            <button type="submit" className="primary-button"><Save size={16} /> 保存资料</button>
          </div>
        </form>
      </section>

      <section className="profile-preferences">
        <div><span className="section-kicker">WORKSPACE NOTE</span><h3>资料是身份层，数据是决策层。</h3></div>
        <p>修改个人资料不会改动仓储数据、库位布局或补货方案。导入数据与空间影像是独立能力，可随时在对应页面替换。</p>
      </section>

      <input
        ref={avatarInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="sr-only"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) importAvatar(file);
          event.target.value = "";
        }}
      />
    </div>
  );
}
